/**
 * Sync / clear TEKS TIME web session JWT on the local Windows Agent companion (localhost only).
 * Clearing the employee JWT must NOT unregister the device or stop monitoring.
 */

const DEFAULT_AGENT_AUTH_URL = 'http://127.0.0.1:17831';

function getAgentAuthBaseUrl(): string {
  const configured = process.env.REACT_APP_AGENT_LOCAL_AUTH_URL;
  return (configured || DEFAULT_AGENT_AUTH_URL).replace(/\/$/, '');
}

export type AgentLocalHealth = {
  ok?: boolean;
  agent?: string;
  version?: string;
  deviceId?: string;
  deviceAuthenticated?: boolean;
};

export async function getWindowsAgentHealth(): Promise<AgentLocalHealth | null> {
  try {
    const response = await fetch(`${getAgentAuthBaseUrl()}/health`, {
      method: 'GET',
      mode: 'cors',
    });
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as AgentLocalHealth;
    return data?.ok ? data : null;
  } catch {
    return null;
  }
}

export async function isWindowsAgentRunning(): Promise<boolean> {
  return Boolean(await getWindowsAgentHealth());
}

/**
 * Ask the local Windows Agent to sign a server-issued challenge nonce, proving this browser runs on
 * the registered physical machine. Returns the signed assertion object, or null if the agent is not
 * present / declines. The agent's private key never leaves the machine; only the signed assertion
 * crosses the localhost bridge.
 */
export async function getDeviceAssertionFromAgent(nonce: string): Promise<unknown | null> {
  if (!nonce) {
    return null;
  }
  try {
    const response = await fetch(
      `${getAgentAuthBaseUrl()}/device-assertion?nonce=${encodeURIComponent(nonce)}`,
      { method: 'GET', mode: 'cors' },
    );
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    // The agent returns { success, assertion } or the assertion object directly.
    return (data && (data.assertion ?? (data.success ? data.data : data))) || null;
  } catch {
    return null;
  }
}

export async function syncTokenToWindowsAgent(token: string): Promise<boolean> {
  if (!token) {
    return false;
  }

  try {
    const response = await fetch(`${getAgentAuthBaseUrl()}/auth`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return Boolean(data?.success);
  } catch {
    // Agent not installed or not running on this PC.
    return false;
  }
}

/**
 * Clear Companion employee authentication after web logout.
 * Does not clear device registration / monitoring tokens.
 */
export async function clearEmployeeSessionFromWindowsAgent(options?: {
  clearRememberedIdentity?: boolean;
}): Promise<boolean> {
  try {
    const response = await fetch(`${getAgentAuthBaseUrl()}/auth/clear`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clearRememberedIdentity: Boolean(options?.clearRememberedIdentity),
      }),
    });
    if (!response.ok) {
      return false;
    }
    const data = await response.json();
    return Boolean(data?.success);
  } catch {
    return false;
  }
}
